import { useMutation } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { FORM_ERROR } from 'final-form';
import React, { useCallback } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';

import useUser from 'common/hooks/useUser';
import { isDefined } from 'common/utils';
import { useCreateCategoriesTracking } from 'modules/menu/hooks/telemetryHooks';
import { batchCreateSubcategories, offeringApiCreateCategories } from 'modules/shared-menu/api';
import { useScopeContext } from 'modules/shared-menu/contexts/ScopeContext';
import { useListingManagerRoutes } from 'modules/shared-menu/hooks/useListingManagerRoutes';
import { useSelectionData } from 'modules/shared-menu/hooks/useSelectionData';
import {
  ImageProcessingError,
  useProcessSelectionImage,
} from 'modules/shared-menu/pages/SharedMenuEditorPage/hooks/useProcessSelectionImage';
import {
  BatchCreateSubcategoriesPayload,
  OfferingApiCreateCategoriesPayload,
  SelectionCategory,
} from 'modules/shared-menu/types';

import { AddCategoriesForm } from './AddCategoriesForm';
import { AddCategoriesFormValues } from './types';
import { buildOfferingApiCategoryPayload } from './utils';

const isExpectedError = (error: unknown) => error instanceof ImageProcessingError || isAxiosError(error);

type ExecuteSubmitArgs = {
  formValues: AddCategoriesFormValues;
};

type ExecuteCreateCategoriesArgs = {
  categories: AddCategoriesFormValues['categories'];
};

type ExecuteCreateSubcategoriesArgs = {
  formValues: AddCategoriesFormValues;
  addedCategories: SelectionCategory[];
};

export const AddCategoriesPage = () => {
  const history = useHistory();
  const intl = useIntl();
  const user = useUser();
  const processSelectionImage = useProcessSelectionImage();
  const { menuEditorPage } = useListingManagerRoutes();
  const { selection, updateSelectionData, categories: existingCategories } = useSelectionData();
  const scope = useScopeContext();

  const trackCreateCategories = useCreateCategoriesTracking();

  const handleCloseClick = useCallback(() => {
    history.push({ pathname: menuEditorPage() });
  }, [history, menuEditorPage]);

  const { mutateAsync: executeCreateCategories } = useMutation(async ({ categories }: ExecuteCreateCategoriesArgs) => {
    const buildCategoryCreateDataPromises = categories.map(async category => {
      const imageUrl = await processSelectionImage(category.image);
      return buildOfferingApiCategoryPayload({
        primaryLanguage: selection.primary_language,
        selectionId: selection.id,
        category,
        imageUrl: imageUrl ?? undefined,
        userId: user.user?.id ?? '',
      });
    });

    const categoryPayloads = (await Promise.all(buildCategoryCreateDataPromises)).filter(isDefined);
    const payload: OfferingApiCreateCategoriesPayload = {
      categories: categoryPayloads,
      request_scope: scope,
    };

    const offeringApiResponse = await offeringApiCreateCategories(payload);

    const categoryNames = offeringApiResponse.categories.map(category => category.name?.[0].value ?? '');
    const hasImage = offeringApiResponse.categories.some(category => !!category.image);
    trackCreateCategories(categoryNames, hasImage);

    const transformedAddedCategories = offeringApiResponse.categories.map(category => {
      const { subcategoryIds, selection_id, author, ...rest } = category;
      const transformedCategory: SelectionCategory = {
        ...rest,
        scope: { id: selection.id, type: 'selection' },
      };
      return transformedCategory;
    });

    return transformedAddedCategories;
  });

  const { mutateAsync: executeCreateSubcategories } = useMutation(
    async ({ formValues, addedCategories }: ExecuteCreateSubcategoriesArgs) => {
      const { categories, childCategoriesRecord } = formValues;

      const subcatPayloadsWithParentCateogryIds = Object.keys(childCategoriesRecord)
        .map(parentCategoryRowId => {
          const validCategorySubcategories = childCategoriesRecord[parentCategoryRowId].filter(
            subcat => !!subcat.name.trim(),
          );

          if (validCategorySubcategories.length === 0) {
            return undefined;
          }

          const parentCategoryName = categories.find(cat => cat._rowId === parentCategoryRowId)?.name ?? '';
          const parentCategoryId = addedCategories.find(cat => cat.name[0].value === parentCategoryName)?.id;

          if (!parentCategoryId) {
            return undefined;
          }

          const subcatPayloads: BatchCreateSubcategoriesPayload[] = validCategorySubcategories
            .map(subcat => {
              return {
                name: [
                  {
                    lang: selection.primary_language,
                    value: subcat.name,
                    verified: true,
                  },
                ],
              };
            })
            .filter(isDefined);

          return {
            parentCategoryId,
            subcatPayloads,
          };
        })
        .filter(isDefined);

      if (subcatPayloadsWithParentCateogryIds.length === 0) {
        return undefined;
      }

      const [firstPayload, ...otherPayloads] = subcatPayloadsWithParentCateogryIds;

      await Promise.all(
        otherPayloads.map(
          async payload =>
            await batchCreateSubcategories(selection.id, payload.parentCategoryId, payload.subcatPayloads),
        ),
      );

      const updatedSelection = await batchCreateSubcategories(
        selection.id,
        firstPayload.parentCategoryId,
        firstPayload.subcatPayloads,
      );

      return updatedSelection;
    },
  );

  const { mutateAsync: executeSubmit } = useMutation(
    async ({ formValues }: ExecuteSubmitArgs) => {
      const { categories, childCategoriesRecord } = formValues;

      const addedCategories = await executeCreateCategories({ categories });

      const hasSubcategories = Object.values(childCategoriesRecord).some(value => {
        const filteredSubcategories = value.filter(v => !!v.name.trim());
        return filteredSubcategories.length > 0;
      });

      if (hasSubcategories) {
        const updatedSelection = await executeCreateSubcategories({ formValues, addedCategories });
        if (updatedSelection) {
          updateSelectionData({ actionType: 'updateSelection', payload: updatedSelection });
        }
      } else {
        const updatedCategories = [...existingCategories, ...addedCategories];
        updateSelectionData({
          actionType: 'updateCategories',
          payload: updatedCategories,
        });
      }
      return addedCategories;
    },
    {
      onError: error => {
        if (!isExpectedError(error)) {
          throw error;
        }
        return { [FORM_ERROR]: intl.formatMessage({ id: 'errors.addCategoryError' }) };
      },
      onSuccess: addedCategories => {
        toast.success(<FormattedMessage id="toast.success" />);

        const redirectPath =
          addedCategories.length > 0 ? `${menuEditorPage()}/${addedCategories[0].id}` : `${`${menuEditorPage()}`}`;

        history.push({ pathname: redirectPath });
      },
    },
  );

  return <AddCategoriesForm onCloseClick={handleCloseClick} onSubmit={formValues => executeSubmit({ formValues })} />;
};
